import React, { useCallback, useEffect, useState } from 'react';
import { History, Save, RotateCcw, Trash2, Loader2, X } from 'lucide-react';
import type { ProposalNodeSet } from '../../types';
import {
  clearStructureTimberLists,
  createStructureTimberVersion,
  formatTimberVersionTimestamp,
  getStructureTimberVersion,
  listStructureTimberVersions,
  materialsPatchFromTimberVersion,
} from '../../utils/proposalTimberVersioning';
import type { StructureTimberListVersion } from '../../types';
import './StructureTimberVersionsPanel.css';

export interface StructureTimberVersionsPanelProps {
  dealId: string;
  nodeSet: ProposalNodeSet;
  disabled?: boolean;
  userId?: string;
  userName?: string;
  onApplyMaterials: (patch: ReturnType<typeof materialsPatchFromTimberVersion>) => void;
}

const StructureTimberVersionsPanel: React.FC<StructureTimberVersionsPanelProps> = ({
  dealId,
  nodeSet,
  disabled = false,
  userId,
  userName,
  onApplyMaterials,
}) => {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState<StructureTimberListVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!dealId || !nodeSet.id) return;
    setLoading(true);
    setError(null);
    try {
      const list = await listStructureTimberVersions(dealId, nodeSet.id);
      setVersions(list);
    } catch (e) {
      console.error(e);
      setError('Could not load timber versions.');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [dealId, nodeSet.id]);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const handleSaveVersion = async () => {
    if (!userId || disabled) return;
    const label = window.prompt('Version label (optional):', '') ?? '';
    setBusy(true);
    setError(null);
    try {
      await createStructureTimberVersion(dealId, nodeSet, {
        label: label.trim() || undefined,
        createdBy: userId,
        createdByName: userName,
      });
      if (open) await refresh();
    } catch (e) {
      console.error(e);
      setError('Could not save version.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevert = async (versionId: string) => {
    if (disabled) return;
    const confirmed = window.confirm(
      'Revert this structure\'s timber lists (all species) to the selected version? Your current lists will be auto-saved first.',
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      if (userId) {
        await createStructureTimberVersion(dealId, nodeSet, {
          label: 'Before revert',
          createdBy: userId,
          createdByName: userName,
        });
      }
      const version = await getStructureTimberVersion(dealId, versionId);
      if (!version) throw new Error('Version not found');
      onApplyMaterials(materialsPatchFromTimberVersion(version));
      if (open) await refresh();
    } catch (e) {
      console.error(e);
      setError('Could not revert to that version.');
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    if (disabled) return;
    const confirmed = window.confirm(
      'Clear the entire timber list and all species for this structure? This cannot be undone (unless you revert from a saved version).',
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      if (userId) {
        await createStructureTimberVersion(dealId, nodeSet, {
          label: 'Before clear all',
          createdBy: userId,
          createdByName: userName,
        });
      }
      onApplyMaterials(clearStructureTimberLists());
      if (open) await refresh();
    } catch (e) {
      console.error(e);
      setError('Could not clear timber lists.');
    } finally {
      setBusy(false);
    }
  };

  if (!dealId) return null;

  return (
    <div className="stv-panel">
      <div className="stv-toolbar">
        <button
          type="button"
          className="stv-btn"
          disabled={disabled || busy}
          onClick={() => void handleSaveVersion()}
          title="Save current timber lists for all species"
        >
          {busy ? <Loader2 size={14} className="stv-spin" /> : <Save size={14} />}
          Save version
        </button>
        <button
          type="button"
          className={`stv-btn ${open ? 'stv-btn-active' : ''}`}
          onClick={() => setOpen(v => !v)}
          title="View saved versions"
        >
          <History size={14} />
          History{versions.length > 0 && open ? ` (${versions.length})` : ''}
        </button>
        <button
          type="button"
          className="stv-btn stv-btn-danger"
          disabled={disabled || busy}
          onClick={() => void handleClearAll()}
          title="Clear default list and all alternate species"
        >
          <Trash2 size={14} />
          Clear all
        </button>
      </div>

      {error && <p className="stv-error" role="alert">{error}</p>}

      {open && (
        <div className="stv-history">
          <div className="stv-history-header">
            <span>Saved versions</span>
            <button type="button" className="stv-close" onClick={() => setOpen(false)} aria-label="Close history">
              <X size={14} />
            </button>
          </div>
          {loading ? (
            <p className="stv-muted">Loading…</p>
          ) : versions.length === 0 ? (
            <p className="stv-muted">No saved versions yet. Use Save version before major edits.</p>
          ) : (
            <ul className="stv-list">
              {versions.map(v => (
                <li key={v.id} className="stv-item">
                  <div className="stv-item-main">
                    <strong>{v.label || 'Untitled version'}</strong>
                    <span className="stv-meta">
                      {formatTimberVersionTimestamp(v)} · {v.speciesSummary} · {v.timberList.length} lines
                      {v.quoteMultipleSpecies && v.alternateSpecies.length > 0
                        ? ` (+${v.alternateSpecies.length} alt.)`
                        : ''}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="stv-revert-btn"
                    disabled={disabled || busy}
                    onClick={() => v.id && void handleRevert(v.id)}
                    title="Revert all species to this version"
                  >
                    <RotateCcw size={13} />
                    Revert
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default StructureTimberVersionsPanel;
